import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseHelpCenterBlocks } from '../../utils/helpCenterBlocks';
import { parseProductBlocks, ProductCardData } from '../../utils/productBlocks';
import { parseOrderSegments } from '../../utils/orderBlocks';
import { parseLookSegments, LookCardData } from '../../utils/lookBlocks';
import HelpCenterCard from '../Widget/HelpCenterCard';
import ProductTile from '../Widget/ProductTile';
import CheckoutLink from '../Widget/CheckoutLink';
import OrderCard from '../Widget/OrderCard';
import LookCard from '../Widget/LookCard';
import { withPosterFrame } from '../../utils/videoAnalysis';

const STOREFRONT_TOKEN_RE =
  /\[(?:CART_ADD|CART_UPDATE|CART_CHANGE|CART_CLEAR|CART_VIEW|DISCOUNT_APPLY|NAVIGATE|GO_TO_CHECKOUT|CUSTOMER_INFO|READ_CUSTOMER|FETCH_PRODUCT)(?::[^\[\]]*)?\]/g;

export function stripStorefrontTokens(text: string): string {
  if (!text) return text;
  return text.replace(STOREFRONT_TOKEN_RE, '').replace(/\n{3,}/g, '\n\n').trim();
}

// Strip bare `VIDEO:<name>` hallucinations (no brackets, no URL) that the LLM
// sometimes emits. Well-formed tokens `[VIDEO:name](url)` are preserved via a
// placeholder swap so the negative-lookbehind isn't needed.
export function stripBareVideoTokens(text: string): string {
  if (!text) return text;
  const wellFormed: string[] = [];
  const withPlaceholders = text.replace(
    /\[VIDEO:[^\]]*\]\([^)]+\)/gi,
    (m) => {
      wellFormed.push(m);
      return `\u0000VID${wellFormed.length - 1}\u0000`;
    }
  );
  const cleaned = withPlaceholders.replace(/VIDEO:[^\n.!?]*/gi, '').replace(/ {2,}/g, ' ');
  return cleaned.replace(/\u0000VID(\d+)\u0000/g, (_, i) => wellFormed[Number(i)]);
}

// Encode whitespace in URLs so <video src> / href accept them.
function encodeUrlSpaces(url: string): string {
  return url.replace(/ /g, '%20');
}

type DetectedVideo =
  | { kind: 'file'; url: string }
  | { kind: 'youtube'; embedUrl: string }
  | { kind: 'vimeo'; embedUrl: string };

export function detectVideoUrl(rawUrl: string): DetectedVideo | null {
  if (!rawUrl) return null;
  try {
    const url = rawUrl.trim();
    // YouTube
    if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtube.com/watch')
        ? new URLSearchParams(new URL(url).search).get('v')
        : url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
      if (videoId) {
        return { kind: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0` };
      }
    }
    // Vimeo
    if (url.includes('vimeo.com/')) {
      const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (match && match[1]) {
        return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${match[1]}` };
      }
    }
    // Direct video file (ignore query string)
    const pathOnly = url.split('?')[0].split('#')[0].toLowerCase();
    if (/\.(mp4|webm|mov|ogg|m4v)$/.test(pathOnly)) {
      return { kind: 'file', url: rawUrl };
    }
  } catch (e) {
    // Ignore URL parsing errors
  }
  return null;
}

const VideoEmbed: React.FC<{ detected: DetectedVideo; title?: string }> = ({ detected, title }) => {
  if (detected.kind === 'file') {
    return (
      <figure className="not-prose my-2">
        <video controls playsInline className="max-w-full rounded-lg" preload="metadata" title={title}>
          {/* #t=0.1 = iOS Safari poster-frame fallback (these never have a poster). */}
          <source src={withPosterFrame(encodeUrlSpaces(detected.url))} />
        </video>
        {title && (
          <figcaption className="text-xs text-gray-500 dark:text-gray-400 mt-1">{title}</figcaption>
        )}
      </figure>
    );
  }
  return (
    <div className="mt-2 mb-2 w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-black aspect-video relative">
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={detected.embedUrl}
        title={title || (detected.kind === 'youtube' ? 'YouTube video player' : 'Vimeo video player')}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowFullScreen
      ></iframe>
    </div>
  );
};

const ProductCarousel: React.FC<{
  items: { data: ProductCardData; idx: number }[];
  brandColor: string;
  isDark: boolean;
  onTryOn?: (data: ProductCardData) => void;
}> = ({ items, brandColor, isDark, onTryOn }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;

    // Re-measure arrows whenever the carousel's own size changes (widget
    // resize, device rotation, parent layout reflow). ResizeObserver catches
    // BOTH the container's clientWidth growing AND scrollWidth shrinking
    // (e.g. an image lazy-loading changes content reflow). window.resize is
    // a belt-and-suspenders fallback for older browsers / iframe edge cases.
    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);
    window.addEventListener('resize', updateArrows);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateArrows);
    };
  }, [items]);

  const scroll = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 140, behavior: 'smooth' });
  };

  const arrowStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 0,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: 'none',
    background: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.55)',
    color: isDark ? '#1f2937' : '#ffffff',
    fontSize: '14px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  });

  return (
    <div className="not-prose" style={{ position: 'relative' }}>
      {showLeft && (
        <button style={arrowStyle('left')} onClick={() => scroll(-1)} aria-label="Scroll left">&#8249;</button>
      )}
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          padding: '4px 0 8px',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {items.map(item => (
          <div key={`pt-${item.idx}`} style={{ scrollSnapAlign: 'start' }}>
            <ProductTile
              {...item.data}
              brandColor={brandColor}
              isDark={isDark}
              onTryOn={onTryOn ? () => onTryOn(item.data) : undefined}
            />
          </div>
        ))}
      </div>
      {showRight && (
        <button style={arrowStyle('right')} onClick={() => scroll(1)} aria-label="Scroll right">&#8250;</button>
      )}
    </div>
  );
};

export interface AgentMessageContentProps {
  content: string;
  role: 'user' | 'agent';
  brandColor?: string;
  isDark?: boolean;
  onArticleClick?: (slug: string) => void;
  aiLabelText?: string;
  customerLabelText?: string;
  showAiLabel?: boolean;
  showCustomerLabel?: boolean;
  /** Virtual Try-On (Phase 3) — passed ONLY by the chat widget (Widget.tsx)
   *  when VTO is enabled. Presence of these callbacks is what gates the
   *  try-on buttons into the chat surface; the admin Inbox and the voice
   *  panel never pass them, so no button appears there. */
  onTryOnSingle?: (data: ProductCardData) => void;
  onTryOnLook?: (data: LookCardData) => void;
}

const AgentMessageContent: React.FC<AgentMessageContentProps> = ({
  content,
  role,
  brandColor = '#3b82f6',
  isDark = false,
  onArticleClick,
  aiLabelText,
  customerLabelText,
  showAiLabel = false,
  showCustomerLabel = false,
  onTryOnSingle,
  onTryOnLook,
}) => {
  const displayContent = stripBareVideoTokens(stripStorefrontTokens(content));
  const segments = role === 'user'
    ? [{ type: 'text' as const, content: displayContent }]
    : parseHelpCenterBlocks(displayContent);
  let isFirstParagraph = true;

  const renderMarkdown = (text: string, key: string): React.ReactNode => (
    <ReactMarkdown
      key={key}
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node, ...props }) => <h1 {...props} style={{ color: role === 'user' ? 'inherit' : (isDark ? '#ffffff' : '#111827') }} />,
        h2: ({ node, ...props }) => <h2 {...props} style={{ color: role === 'user' ? 'inherit' : (isDark ? '#ffffff' : '#111827') }} />,
        h3: ({ node, ...props }) => <h3 {...props} style={{ color: role === 'user' ? 'inherit' : (isDark ? '#ffffff' : '#1f2937') }} />,
        h4: ({ node, ...props }) => <h4 {...props} style={{ color: role === 'user' ? 'inherit' : (isDark ? '#ffffff' : '#1f2937') }} />,
        // LEGACY - remove after Option C stable: fallback VIDEO token
        // detection for pre-Option-C messages still in history.
        a: ({ node, ...props }) => {
          const href = props.href || '';
          const labelText = React.Children.toArray(props.children)
            .map(c => (typeof c === 'string' ? c : ''))
            .join('')
            .trim();
          const isVideoToken = /^VIDEO:\s*/i.test(labelText);
          const cleanLabel = isVideoToken ? labelText.replace(/^VIDEO:\s*/i, '').trim() : labelText;
          const detected = detectVideoUrl(href)
            || (isVideoToken ? { kind: 'file' as const, url: href } : null);
          if (detected) {
            return <VideoEmbed detected={detected} title={cleanLabel || undefined} />;
          }
          return role === 'user'
            ? <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: 'currentColor', opacity: 0.9 }} />
            : <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 underline" />;
        },
        code: ({ node, className, children, ...props }) => {
          const isBlock = /language-/.test(className || '');
          if (isBlock) {
            return <code {...props} className={className}>{children}</code>;
          }
          return (
            <code
              {...props}
              style={{
                color: isDark ? '#A020F0' : '#6b21a8',
                backgroundColor: isDark ? '#1f2937' : 'transparent',
                padding: isDark ? '0.125rem 0.375rem' : 0,
                borderRadius: isDark ? '0.25rem' : undefined,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.875em',
              }}
            >
              {children}
            </code>
          );
        },
        img: ({ src, alt }) => (
          <a href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt={alt || ''} className="max-w-full h-auto rounded-lg my-2 cursor-pointer" />
          </a>
        ),
        p: ({ children, ...props }) => {
          const showLabel = isFirstParagraph;
          isFirstParagraph = false;
          const childArray = React.Children.toArray(children);
          const processedChildren = childArray.map((child, i) => {
            if (typeof child === 'string') {
              // Matches [VIDEO:name](url) or plain [name](url); URLs may contain spaces.
              const linkRegex = /\[([^\]]*)\]\(([^)]+)\)/g;
              const parts: React.ReactNode[] = [];
              let lastIndex = 0;
              let match;
              let replaced = false;
              while ((match = linkRegex.exec(child)) !== null) {
                const rawLabel = match[1];
                const url = match[2];
                const isVideoToken = /^VIDEO:\s*/i.test(rawLabel);
                const cleanLabel = isVideoToken ? rawLabel.replace(/^VIDEO:\s*/i, '').trim() : rawLabel.trim();
                const detected = detectVideoUrl(url)
                  || (isVideoToken ? { kind: 'file' as const, url } : null);
                if (!detected) continue;
                if (match.index > lastIndex) {
                  parts.push(child.substring(lastIndex, match.index));
                }
                parts.push(
                  <VideoEmbed key={`v-${i}-${match.index}`} detected={detected} title={cleanLabel || undefined} />
                );
                lastIndex = match.index + match[0].length;
                replaced = true;
              }
              if (replaced) {
                if (lastIndex < child.length) parts.push(child.substring(lastIndex));
                return <React.Fragment key={i}>{parts}</React.Fragment>;
              }
            }
            return child;
          });
          return (
            <p {...props} style={{ color: role === 'user' ? 'inherit' : (isDark ? '#e5e7eb' : '#374151') }}>
              {showLabel && role !== 'user' && showAiLabel && aiLabelText && <strong className="mr-1">{aiLabelText}:</strong>}
              {showLabel && role === 'user' && showCustomerLabel && customerLabelText && <strong className="mr-1" style={{ color: 'inherit' }}>{customerLabelText}:</strong>}
              {processedChildren}
            </p>
          );
        }
      }}
    >
      {text}
    </ReactMarkdown>
  );

  // Splits a text chunk on [LOOK:{...}] markers and returns interleaved
  // LookCard + markdown nodes. Innermost splitter — any text remnant
  // left after ORDER_CARD splitting is passed through here so LOOK,
  // ORDER and PRODUCT cards can all coexist in one bubble.
  const renderTextWithLookCards = (text: string, keyPrefix: string): React.ReactNode[] => {
    const lookSegs = parseLookSegments(text);
    const nodes: React.ReactNode[] = [];
    lookSegs.forEach((ls, li) => {
      if (ls.type === 'look_card') {
        nodes.push(
          <div key={`${keyPrefix}-lk-${li}`} className="not-prose">
            <LookCard
              data={ls.data}
              brandColor={brandColor}
              isDark={isDark}
              onTryOnLook={onTryOnLook ? () => onTryOnLook(ls.data) : undefined}
            />
          </div>
        );
      } else if (ls.content && ls.content.trim()) {
        nodes.push(renderMarkdown(ls.content, `${keyPrefix}-md-${li}`));
      }
    });
    return nodes;
  };

  // Splits a text chunk on [ORDER_CARD:{...}] markers and returns
  // interleaved OrderCard + (LOOK/markdown) nodes. Used by both the
  // product-bearing branch (for text remnants between PRODUCT_CARDs)
  // and the plain-text fallback branch. Text remnants are further
  // split on [LOOK:{...}] markers via renderTextWithLookCards.
  const renderTextWithOrderCards = (text: string, keyPrefix: string): React.ReactNode[] => {
    const orderSegs = parseOrderSegments(text);
    const nodes: React.ReactNode[] = [];
    orderSegs.forEach((os, oi) => {
      if (os.type === 'order_card') {
        nodes.push(
          <div key={`${keyPrefix}-oc-${oi}`} className="not-prose">
            <OrderCard data={os.data} brandColor={brandColor} isDark={isDark} />
          </div>
        );
      } else if (os.content && os.content.trim()) {
        nodes.push(...renderTextWithLookCards(os.content, `${keyPrefix}-${oi}`));
      }
    });
    return nodes;
  };

  return (
    <>
      {segments.map((seg, segIdx) => {
        if (seg.type === 'card') {
          return (
            <div key={`seg-${segIdx}`} className="not-prose">
              <HelpCenterCard
                article_id={seg.data.article_id}
                title={seg.data.title}
                description={seg.data.description}
                slug={seg.data.slug}
                url={seg.data.url}
                thumbnail={seg.data.thumbnail}
                brandColor={brandColor}
                isDark={isDark}
                onReadClick={(slug) => onArticleClick?.(slug)}
              />
            </div>
          );
        }
        if (seg.type === 'page') {
          return (
            <div key={`seg-${segIdx}`} className="not-prose">
              <HelpCenterCard
                article_id={seg.data.article_id}
                title={seg.data.title}
                description=""
                slug={seg.data.slug}
                url={seg.data.url}
                brandColor={brandColor}
                isDark={isDark}
                onReadClick={(slug) => onArticleClick?.(slug)}
              />
            </div>
          );
        }
        if (seg.type === 'media') {
          const md = seg.data;
          if (md.type === 'image') {
            return <div key={`seg-${segIdx}`} className="not-prose"><img src={md.url} alt={md.alt || ''} className="max-w-full h-auto rounded-lg my-2" /></div>;
          }
          if (md.type === 'video') {
            const detected = detectVideoUrl(md.url) || { kind: 'file' as const, url: md.url };
            return (
              <div key={`seg-${segIdx}`} className="not-prose">
                <VideoEmbed detected={detected} title={md.caption || md.alt} />
              </div>
            );
          }
          return null;
        }

        const productSegs = parseProductBlocks(seg.content as string);
        const hasProductBlocks = productSegs.some(ps => ps.type !== 'text');
        if (hasProductBlocks) {
          const grouped: Array<{ kind: 'tiles'; items: { data: ProductCardData; idx: number }[] } | { kind: 'other'; node: React.ReactNode }> = [];
          for (let pi = 0; pi < productSegs.length; pi++) {
            const ps = productSegs[pi];
            if (ps.type === 'product_card') {
              const last = grouped[grouped.length - 1];
              if (last && last.kind === 'tiles') {
                last.items.push({ data: ps.data, idx: pi });
              } else {
                grouped.push({ kind: 'tiles', items: [{ data: ps.data, idx: pi }] });
              }
            } else if (ps.type === 'checkout_link') {
              grouped.push({ kind: 'other', node: (
                <div key={`ps-${pi}`} className="not-prose">
                  <CheckoutLink {...ps.data} isDark={isDark} />
                </div>
              )});
            } else if (ps.content && (ps.content as string).trim()) {
              const orderNodes = renderTextWithOrderCards(ps.content as string, `ps-${pi}`);
              if (orderNodes.length > 0) {
                grouped.push({ kind: 'other', node: (
                  <React.Fragment key={`ps-${pi}`}>{orderNodes}</React.Fragment>
                )});
              }
            }
          }
          return (
            <React.Fragment key={`seg-${segIdx}`}>
              {grouped.map((g, gi) => {
                if (g.kind === 'tiles') {
                  return (
                    <ProductCarousel key={`tg-${gi}`} items={g.items} brandColor={brandColor} isDark={isDark} onTryOn={onTryOnSingle} />
                  );
                }
                return <React.Fragment key={`tg-${gi}`}>{g.node}</React.Fragment>;
              })}
            </React.Fragment>
          );
        }

        const textContent = seg.content;
        return (
          <React.Fragment key={`seg-${segIdx}`}>
            {renderTextWithOrderCards(textContent, `seg-${segIdx}`)}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default AgentMessageContent;
